import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import type { Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { useDialog } from '@/components/dialogs/useDialog';
import type { PaymentRecord, PaymentDialogResult, PaymentsFilter } from '@/types/monitoring';
import { usePaymentsQuery } from './api';
import { PaymentDetailsModal } from './PaymentDetailsModal';
import { getProviderIconSrc } from './providerIcon';

const AUTO_REFRESH_MS = 30_000;
const DEFAULT_FILTER: PaymentsFilter = { nbPayments: 20, startDate: null, endDate: null };

function rowBg(status: number): string {
  if (status === 0) return 'success.light';
  if (status === 50) return 'warning.light';
  return 'error.light';
}

export function PaymentMonitoringPage() {
  const { t } = useTranslation();
  const { open: openDialog } = useDialog();

  // Form state
  const [nbPayments, setNbPayments] = useState(20);
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Committed filter drives the query
  const [committedFilter, setCommittedFilter] = useState<PaymentsFilter>(DEFAULT_FILTER);

  const isFilterValid =
    (startDate === null && endDate === null) ||
    (startDate !== null && endDate !== null && !startDate.isAfter(endDate));

  const query = usePaymentsQuery(committedFilter, autoRefresh ? AUTO_REFRESH_MS : false);

  const allRows: PaymentRecord[] = useMemo(
    () => query.data?.payments ?? [],
    [query.data],
  );

  // Client-side text filter
  const [textFilter, setTextFilter] = useState('');
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const filtered = useMemo(() => {
    if (!textFilter.trim()) return allRows;
    const term = textFilter.toLowerCase();
    return allRows.filter(
      (p) =>
        (p.id && p.id.toString().includes(term)) ||
        (p.userAlias && p.userAlias.toLowerCase().includes(term)) ||
        (p.providerAccountId && p.providerAccountId.toLowerCase().includes(term)) ||
        (p.shop && p.shop.toLowerCase().includes(term)) ||
        (p.operatorAccountNumber && p.operatorAccountNumber.toString().includes(term)) ||
        (p.amount && p.amount.toString().includes(term)),
    );
  }, [allRows, textFilter]);

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleFetch = () => {
    setTextFilter('');
    setPage(0);
    setCommittedFilter({
      nbPayments,
      startDate: startDate ? startDate.toISOString() : null,
      endDate: endDate ? endDate.toISOString() : null,
    });
  };

  const handleReset = () => {
    setNbPayments(20);
    setStartDate(null);
    setEndDate(null);
    setTextFilter('');
    setPage(0);
    setCommittedFilter({ ...DEFAULT_FILTER });
  };

  const handleRowClick = async (payment: PaymentRecord) => {
    const result = await openDialog<PaymentDialogResult | null>((close) => (
      <PaymentDetailsModal payment={payment} onClose={close} />
    ));
    if (!result) return;
    if (result.code === 0) {
      const msg =
        result.type === 'cancel'
          ? t('pages.monitoring.payments.payment_cancelled_successfully', {
              defaultValue: 'Payment cancelled successfully.',
            })
          : t('pages.monitoring.payments.payment_corrected_successfully', {
              defaultValue: 'Payment corrected successfully.',
            });
      await Swal.fire(
        t('pages.monitoring.payments.success', { defaultValue: 'Success' }),
        msg,
        'success',
      );
      handleReset();
    } else {
      await Swal.fire(
        t('messages.error', { defaultValue: 'Error' }),
        t('pages.monitoring.payments.payment_has_pending_transaction', {
          defaultValue: 'Payment has a pending transaction.',
        }),
        'error',
      );
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t('menus.monitoring_payments', { defaultValue: 'Payment Monitoring' })}
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'flex-end' }}
          flexWrap="wrap"
        >
          <TextField
            label={t('pages.monitoring.payments.nb_payments', {
              defaultValue: 'Number of payments',
            })}
            type="number"
            size="small"
            value={nbPayments}
            onChange={(e) => setNbPayments(Number(e.target.value))}
            sx={{ minWidth: 160 }}
          />
          <DateTimePicker
            label={t('pages.monitoring.corrections.start_date', { defaultValue: 'Start date' })}
            value={startDate}
            onChange={(val) => setStartDate(val)}
            slotProps={{ textField: { size: 'small' } }}
          />
          <DateTimePicker
            label={t('pages.monitoring.corrections.end_date', { defaultValue: 'End date' })}
            value={endDate}
            onChange={(val) => setEndDate(val)}
            slotProps={{ textField: { size: 'small' } }}
          />
          <Button variant="contained" disabled={!isFilterValid} onClick={handleFetch}>
            {t('pages.monitoring.payments.fetch', { defaultValue: 'Fetch' })}
          </Button>
          <Button variant="contained" color="error" onClick={handleReset}>
            {t('pages.monitoring.payments.reset', { defaultValue: 'Reset' })}
          </Button>
        </Stack>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
            }
            label={t('pages.monitoring.payments.auto_refresh', { defaultValue: 'Auto-refresh' })}
          />
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <TextField
          size="small"
          value={textFilter}
          onChange={(e) => {
            setTextFilter(e.target.value);
            setPage(0);
          }}
          placeholder={t('pages.user-list.filter', { defaultValue: 'Filter…' })}
          sx={{ mb: 2, minWidth: 280 }}
        />

        {query.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t('pages.monitoring.payments.table.date', { defaultValue: 'Date' })}
                    </TableCell>
                    <TableCell>
                      {t('pages.monitoring.payments.table.operation', {
                        defaultValue: 'Operation',
                      })}
                    </TableCell>
                    <TableCell>
                      {t('pages.monitoring.payments.table.sender', { defaultValue: 'Sender' })}
                    </TableCell>
                    <TableCell>
                      {t('pages.monitoring.payments.table.receiver', {
                        defaultValue: 'Receiver',
                      })}
                    </TableCell>
                    <TableCell>
                      {t('pages.monitoring.payments.table.shop', { defaultValue: 'Shop' })}
                    </TableCell>
                    <TableCell>
                      {t('pages.monitoring.payments.table.operation_account_number', {
                        defaultValue: 'Account #',
                      })}
                    </TableCell>
                    <TableCell>
                      {t('pages.monitoring.payments.table.amount', { defaultValue: 'Amount' })}
                    </TableCell>
                    <TableCell>
                      {t('pages.monitoring.payments.table.internal_reference', {
                        defaultValue: 'Ref',
                      })}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((row) => {
                    const iconSrc = getProviderIconSrc(row.providerIdentifier);
                    return (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{ bgcolor: rowBg(row.status), cursor: 'pointer' }}
                        onClick={() => void handleRowClick(row)}
                      >
                        <TableCell>
                          {iconSrc && (
                            <Box
                              component="img"
                              src={iconSrc}
                              alt="provider"
                              sx={{ width: 20, height: 20, mr: 0.5, verticalAlign: 'middle' }}
                            />
                          )}
                          {new Date(row.date).toLocaleString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </TableCell>
                        <TableCell>
                          {t('pages.monitoring.payments.payment', { defaultValue: 'Payment' })}
                        </TableCell>
                        <TableCell>{row.providerAccountId}</TableCell>
                        <TableCell>{row.userAlias}</TableCell>
                        <TableCell>{row.shop}</TableCell>
                        <TableCell>{row.operatorAccountNumber}</TableCell>
                        <TableCell>{row.amount}</TableCell>
                        <TableCell>{row.id}</TableCell>
                      </TableRow>
                    );
                  })}
                  {paged.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          {t('pages.monitoring.payments.no_payments_found', {
                            defaultValue: 'No payments found.',
                          })}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[rowsPerPage]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
